import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {chatbotApi, useLazyAskQuestionQuery} from 'modules/chatBot/chatBotApi'
import {
  getAnonymousId,
  getCurrencyCode,
  getLanguageCode
} from 'modules/meta/selectors'
import {v4 as uuid} from 'uuid'

import {Box} from '@vio/atlas/helpers/Box'
import {
  ANIMATION_TYPES,
  Overlay
} from '@vio/atlas/src/components/designSystem/Overlay'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'

import {HotelQuestionContent} from './HotelQuestionContent'
import {HotelQuestionFooter} from './HotelQuestionFooter'
import {HotelQuestionHeader} from './HotelQuestionHeader'
import {Message, MessageType} from './types'
import {hasReachedDailyMessageLimit, incrementMessageCount} from './utils'

const DESKTOP_OVERLAY_WIDTH = 500

export const HotelQuestionModal = ({
  hotelName,
  isOpen,
  onClose
}: {
  hotelName: string
  isOpen: boolean
  onClose: () => void
}) => {
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)
  const currencyCode = useSelector(getCurrencyCode)
  const anonymousId = useSelector(getAnonymousId)
  const [conversation, setConversation] = useState<string | null>(null)
  const conversationIdTracking = useRef<string | null>(uuid())
  const hotelId = useSelector(getAccommodationPageHotelId)
  const [messages, setMessages] = useState<Message[]>([])
  const scrollRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState('')
  const {isMobile} = useDeviceLayout()
  const dispatch = useDispatch()
  const [fetchAnswer, {isFetching}] = useLazyAskQuestionQuery()

  const resetChatbotData = useCallback(() => {
    dispatch(chatbotApi.util.resetApiState())
  }, [dispatch])

  const hotelRequestParams = {
    languageCode,
    currencyCode,
    anonymousId,
    hotelId
  }
  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    })
  }, [])

  const handleSubmitQuestion = useCallback(async () => {
    const question = inputValue?.trim()
    if (!question) return
    setMessages(prev => [...prev, {type: MessageType.QUESTION, text: question}])
    incrementMessageCount()
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.ChatbotSendButton,
        action: Action.Clicked,
        team: Team.Select,
        payload: {
          message: question,
          conversationId: conversationIdTracking.current,
          messagePosition: messages.length + 1
        }
      })
    )
    setInputValue('')
    if (hasReachedDailyMessageLimit()) {
      setMessages(prev => [
        ...prev,
        {
          type: MessageType.ANSWER,
          text: formatMessage({
            id: 'chatbot.chatLimitReachedText',
            defaultMessage:
              'You’ve reached today’s chat limit. You can still explore more details about this property on the page or come back later to ask more.'
          })
        }
      ])
      return
    }
    const {data: {answer, conversation: conversationId} = {}} =
      await fetchAnswer({
        question,
        hotelRequestParams,
        conversation
      })
    if (answer) {
      setMessages(prev => [...prev, {type: MessageType.ANSWER, text: answer}])
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.ChatbotResponse,
          action: Action.Loaded,
          team: Team.Select,
          payload: {
            message: answer,
            conversationId: conversationIdTracking.current,
            messagePosition: messages.length + 2
          }
        })
      )

      setConversation(conversationId)
    }
  }, [
    inputValue,
    dispatch,
    messages.length,
    fetchAnswer,
    hotelRequestParams,
    conversation
  ])

  const handleCloseModal = useCallback(() => {
    resetChatbotData()
    setInputValue('')
    onClose()
  }, [onClose, resetChatbotData])

  const overlayContainerStyles = useMemo(
    () => ({
      width: isMobile ? '100vw' : DESKTOP_OVERLAY_WIDTH,
      marginLeft: isMobile ? '0' : `calc(100vw - ${DESKTOP_OVERLAY_WIDTH}px)`
    }),
    [isMobile]
  )

  useEffect(() => {
    if (messages.length) scrollToBottom()
  }, [scrollToBottom, messages.length])

  return (
    <Overlay
      isOpen={isOpen}
      containerStyles={overlayContainerStyles}
      shadeBackground
      closeOnEsc
      onClose={onClose}
      onShadeBackgroundClick={onClose}
      transitionDuration={300}
      animationType={
        isMobile ? ANIMATION_TYPES.SLIDE_DOWN : ANIMATION_TYPES.SLIDE_RIGHT
      }
    >
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        textAlign="center"
      >
        <HotelQuestionHeader onClose={handleCloseModal} />
        <HotelQuestionContent
          isFetching={isFetching}
          hotelName={hotelName}
          messages={messages}
          scrollRef={scrollRef}
          scrollToBottom={scrollToBottom}
        />
        <HotelQuestionFooter
          onSubmit={handleSubmitQuestion}
          inputValue={inputValue}
          handleInputChange={setInputValue}
        />
      </Box>
    </Overlay>
  )
}
